export const OCTAPHARMA_COMPANY_ID = '61c33c8a671cf50d6bd8dba4';
export const CORPORATE_STUDY_NAMES = ['One-to-one', 'Registre'];

const COMPETITOR_COLORS = ['#44CDEB', '#2C98B0', '#4DCD98', '#24875E', '#FF8469', '#9E3821'];

export function matchCorporateStudyName(name) {
  if (!name) return null;
  const normalized = name.trim().toLowerCase();
  return CORPORATE_STUDY_NAMES.find(item => item.toLowerCase() === normalized) || null;
}

export function isOctapharmaStudy(study) {
  return study?.company?.toString() === OCTAPHARMA_COMPANY_ID;
}

export function getCompanyName(companyId, companies = []) {
  if (!companyId) return 'Inconnu';
  const found = companies.find(c => c.value === companyId.toString());
  return found ? found.label : 'Inconnu';
}

export function getTheoreticalTarget(study) {
  if (!study?.centers?.length) return 0;
  let maxValue = 0;
  study.centers.forEach(center => {
    if (!center.inclusion?.length) return;
    center.inclusion.forEach(item => {
      const value = parseFloat(item.value) || 0;
      if (value > maxValue) maxValue = value;
    });
  });
  return maxValue;
}

export function getTheoreticalAtDate(study, date = new Date()) {
  if (!study?.centers?.length) return 0;
  let total = 0;
  const targetMonth = date.getFullYear() * 12 + date.getMonth();

  study.centers.forEach(center => {
    if (!center.inclusion?.length) return;
    let closest = null;
    center.inclusion.forEach(item => {
      if (!item.date) return;
      const itemDate = new Date(item.date);
      const itemMonth = itemDate.getFullYear() * 12 + itemDate.getMonth();
      if (itemMonth <= targetMonth) {
        if (!closest || itemMonth > closest.month) {
          closest = { month: itemMonth, value: parseFloat(item.value) || 0 };
        }
      }
    });
    if (closest) total += closest.value;
  });

  return total;
}

export function getRecruitmentRate(study) {
  const actual = study?.patients?.length || 0;
  const theoretical = getTheoreticalAtDate(study);
  if (!theoretical) return null;
  return Math.round((actual / theoretical) * 100);
}

function getCorporateStudies(studies) {
  return studies.filter(study => {
    if (!isOctapharmaStudy(study)) return false;
    return !!matchCorporateStudyName(study.name);
  });
}

export function getRecruitmentKpi(studies, corporateName) {
  const study = getCorporateStudies(studies).find(item => matchCorporateStudyName(item.name) === corporateName);
  if (!study) return { actual: 0, target: 0, label: '—' };
  const actual = study.patients?.length || 0;
  const target = getTheoreticalTarget(study);
  return {
    actual,
    target,
    label: target ? `${actual}/${target}` : `${actual}`
  };
}

export function getRateKpi(studies, corporateName) {
  const study = getCorporateStudies(studies).find(item => matchCorporateStudyName(item.name) === corporateName);
  if (!study) return '—';
  const rate = getRecruitmentRate(study);
  return rate === null ? '—' : `${rate}%`;
}

export function getCorporateKpi(studies, corporateName) {
  const study = getCorporateStudies(studies).find(item => matchCorporateStudyName(item.name) === corporateName);
  if (!study) return { studies: 0, patients: 0, label: '0/0' };
  const patients = study.patients?.length || 0;
  return {
    studies: 1,
    patients,
    label: `1/${patients}`
  };
}

export function getCompetitorChartData(studies, companies = []) {
  const competitorStudies = studies.filter(study => study.company?.toString() !== OCTAPHARMA_COMPANY_ID);
  const areaMap = {};

  competitorStudies.forEach(study => {
    const areas = study.therapeuticArea?.length ? study.therapeuticArea : [{ name: 'Autre', _id: 'other' }];
    areas.forEach(area => {
      const areaName = area.name || 'Autre';
      if (!areaMap[areaName]) areaMap[areaName] = {};
      const companyId = study.company?.toString() || 'unknown';
      if (!areaMap[areaName][companyId]) {
        areaMap[areaName][companyId] = { studyCount: 0, patientCount: 0, companyId };
      }
      areaMap[areaName][companyId].studyCount += 1;
      areaMap[areaName][companyId].patientCount += study.patients?.length || 0;
    });
  });

  return Object.keys(areaMap).sort().map((areaName, areaIndex) => {
    const companiesData = Object.values(areaMap[areaName]).map((entry, index) => ({
      companyName: getCompanyName(entry.companyId, companies),
      studyCount: entry.studyCount,
      patientCount: entry.patientCount,
      color: COMPETITOR_COLORS[(areaIndex + index) % COMPETITOR_COLORS.length]
    }));

    const maxPatients = Math.max(...companiesData.map(item => item.patientCount), 1);

    return {
      areaName,
      companies: companiesData,
      maxPatients
    };
  });
}

export function getStudyTimelineBounds(study) {
  if (!study?.steps?.length) return null;
  const dates = study.steps
    .flatMap(step => [step.startDate, step.endDate])
    .filter(Boolean)
    .map(date => new Date(date))
    .filter(date => !Number.isNaN(date.getTime()));

  if (!dates.length) return null;

  return {
    startDate: new Date(Math.min(...dates.map(d => d.getTime()))),
    endDate: new Date(Math.max(...dates.map(d => d.getTime()))),
    steps: study.steps
      .filter(step => step.startDate && step.endDate)
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
  };
}

export function getTimelineStudies(studies) {
  return studies
    .map(study => {
      const bounds = getStudyTimelineBounds(study);
      if (!bounds) return null;
      return { ...study, timeline: bounds };
    })
    .filter(Boolean);
}

export function filterTimelineStudies(studies, { search, types, therapeuticArea }) {
  return studies.filter(study => {
    if (search && !study.name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (types?.length && !types.includes(study.type)) return false;
    if (therapeuticArea && !study.therapeuticArea?.some(area => area._id?.toString() === therapeuticArea.toString())) return false;
    return true;
  });
}

export function buildTimelineGrid(studies, monthZoom = 1) {
  const timelineStudies = getTimelineStudies(studies);
  if (!timelineStudies.length) return null;

  const allDates = timelineStudies.flatMap(study => [study.timeline.startDate, study.timeline.endDate]);
  const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
  const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));

  minDate.setDate(1);
  maxDate.setMonth(maxDate.getMonth() + 1);
  maxDate.setDate(0);

  const months = [];
  const cursor = new Date(minDate);
  const monthLabels = ['jan', 'fév', 'mar', 'avr', 'mai', 'juin', 'juil', 'août', 'sept', 'oct', 'nov', 'déc'];

  while (cursor <= maxDate) {
    months.push({
      label: monthLabels[cursor.getMonth()],
      year: cursor.getFullYear(),
      date: new Date(cursor)
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }

  const totalDays = Math.max(1, Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)));
  const monthWidth = 95 * monthZoom;

  return {
    minDate,
    maxDate,
    months,
    totalDays,
    monthWidth,
    monthZoom,
    canvasWidth: months.length * monthWidth,
    dayWidth: (months.length * monthWidth) / totalDays,
    timelineStudies
  };
}

export function formatTimelineMonthLabel(month) {
  const label = month.label;
  if (month.date.getMonth() === 0) {
    return `${label} ${String(month.year).slice(-2)}`;
  }
  return label;
}

export function formatActivityDate(date) {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';
  const day = String(parsed.getDate()).padStart(2, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const year = String(parsed.getFullYear()).slice(-2);
  return `${day}.${month}.${year}`;
}
